/**
 * The HTTP client. One place that knows about `X-Session-Token`, about
 * `application/problem+json`, and about the fact that a failed request is a
 * *state*, not an exception to be logged and forgotten.
 *
 * Every failure leaves this module as a `Problem` (contracts/api.md §1), so a
 * caller renders from `problem.state` and never has to know whether the
 * failure came from the server, the network, or a parse.
 */

import type {
  AutosyncResponse,
  JobResponse,
  PlatformsResponse,
  Problem,
  QuotaResponse,
  SubmitResponse,
  TranscriptDocument,
} from './api-types.ts';
import { clearSession, SessionError, sessionToken } from './session.ts';

/** Thrown by every helper here. `problem` is always populated. */
export class ApiError extends Error {
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(`${problem.code} (${problem.status})`);
    this.name = 'ApiError';
    this.problem = problem;
  }
}

/**
 * Synthetic problems for failures that never reached the server.
 *
 * ⚠ DRAFT-FOR-FOUNDER: docs/USER_JOURNEY.md has no state for "the request did
 * not complete" — every E-state assumes a server answered. `NETWORK` and
 * `CLIENT` are escalated as DECISION_NEEDED in comms/UI.md; until they are
 * answered they render the draft banner in states.html, which is the only
 * unapproved user-visible copy on the site.
 */
function synthetic(code: string, detail: string, status = 0): Problem {
  return {
    type: `about:blank#${code.toLowerCase().replaceAll('_', '-')}`,
    title: code,
    status,
    detail,
    code,
    state: null,
  };
}

const DEFAULT_TIMEOUT_MS = 15_000;

export interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  /** multipart bodies go here instead; `body` is ignored when set. */
  form?: FormData;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Routes that must not mint a session (POST /api/session itself). */
  anonymous?: boolean;
}

function isProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate['code'] === 'string' && typeof candidate['status'] === 'number';
}

/**
 * `Retry-After`, in seconds.
 *
 * contracts/api.md describes the header as "in seconds", and the origin does
 * send it that way — but RFC 9110 §10.2.3 also permits an HTTP-date, and the
 * edge is entitled to generate its own 429/503 in front of the origin. A
 * seconds-only parser turns that form into NaN, which is a client that either
 * stalls forever or hot-loops, in both cases precisely when the server is
 * already overloaded. Both syntaxes, then.
 */
export function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number(header.trim());
  if (Number.isFinite(seconds)) return seconds > 0 ? seconds : null;
  const at = Date.parse(header);
  if (Number.isNaN(at)) return null;
  const delta = Math.round((at - Date.now()) / 1000);
  return delta > 0 ? delta : null;
}

async function readProblem(res: Response, upload = false): Promise<Problem> {
  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    /* an error response that is not JSON is still an error */
  }
  if (isProblem(parsed)) {
    const retryAfter = parseRetryAfter(res.headers.get('retry-after'));
    return retryAfter === null ? parsed : { ...parsed, retry_after_s: retryAfter };
  }
  return problemForBareStatus(res.status, upload);
}

/**
 * A status that came back without a problem+json body — which means the edge
 * answered, not the app.
 *
 * One of them has a meaning worth keeping. Cloudflare's free plan refuses a
 * request body over 100 MB with its own HTML 413 before the app sees it
 * (measured live, 2026-10-01: 101 MiB, "413 Payload Too Large ... cloudflare"
 * after 18 s). Reported as BAD_RESPONSE that became the NETWORK banner, "check
 * your connection and try again", which no retry could ever fix. On an upload
 * it is the size limit, and says so.
 */
export function problemForBareStatus(status: number, upload: boolean): Problem {
  if (upload && status === 413) {
    return synthetic('UPLOAD_TOO_LARGE', '413 from the edge, before the app', 413);
  }
  return synthetic('BAD_RESPONSE', `${status} without a problem+json body`, status);
}

/**
 * Exported for lib/gemini.ts, whose routes (contracts/api.md 1.19.0) answer 202 and are then
 * polled; every other caller uses the named helpers below.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, form, timeoutMs = DEFAULT_TIMEOUT_MS, anonymous = false } = options;

  const headers: Record<string, string> = {};
  if (!anonymous) {
    try {
      headers['X-Session-Token'] = await sessionToken();
    } catch (err) {
      // A session we could not mint is E14, and it is the honest report: we
      // could not verify the browser, so nothing was attempted.
      throw new ApiError({
        ...synthetic('TURNSTILE_FAILED', err instanceof Error ? err.message : 'no session', 403),
        state: 'E14',
        status: 403,
      });
    }
  }
  if (form === undefined && body !== undefined) headers['content-type'] = 'application/json';

  // One controller for our timeout, linked to any caller-supplied signal.
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new DOMException('timeout', 'TimeoutError')),
    timeoutMs,
  );
  const abortFromCaller = () => controller.abort(options.signal?.reason);
  options.signal?.addEventListener('abort', abortFromCaller, { once: true });

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: form ?? (body === undefined ? null : JSON.stringify(body)),
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
  } catch (err) {
    if (options.signal?.aborted) throw err;
    const timedOut = err instanceof DOMException && err.name === 'TimeoutError';
    throw new ApiError(
      synthetic(
        timedOut ? 'TIMEOUT' : 'NETWORK',
        err instanceof Error ? err.message : 'request failed',
      ),
    );
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', abortFromCaller);
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    const problem = await readProblem(res, form !== undefined);
    // A rejected token is indistinguishable from no token, and the fix for
    // both is a fresh mint. Retry exactly once so a stale localStorage entry
    // is self-healing rather than a permanent E14 the user must clear storage
    // to escape.
    if (!anonymous && (res.status === 401 || problem.code === 'SESSION_INVALID')) {
      clearSession();
      return request<T>(path, { ...options, anonymous: false, timeoutMs });
    }
    throw new ApiError(problem);
  }

  try {
    return (await res.json()) as T;
  } catch (err) {
    throw new ApiError(
      synthetic(
        'BAD_RESPONSE',
        err instanceof Error ? err.message : 'unparseable body',
        res.status,
      ),
    );
  }
}

/* ── the ten routes, as functions ─────────────────────────────────────── */

/**
 * How long a link submission is waited on. Not DEFAULT_TIMEOUT_MS: a YouTube
 * link the server's own address is walled from goes through a relay container,
 * and measured live on 2026-10-05 that took about 15 s, exactly where the old
 * limit cut it off and the page said it "couldn't reach the server" while the
 * server was about to answer. The server bounds its side at about 30 s
 * (server/pipeline/metadata.py RELAY_PROBE_WITHIN_S plus the caption wait).
 */
const SUBMIT_TIMEOUT_MS = 45_000;

export function submitUrl(
  url: string,
  signal?: AbortSignal,
  translateTo?: string,
): Promise<SubmitResponse> {
  return request<SubmitResponse>('/api/transcribe', {
    method: 'POST',
    // `translate_to` (contracts/api.md 1.18.0) only from a page that translates: every other
    // page's body is byte for byte what it was.
    body: translateTo ? { url, translate_to: translateTo } : { url },
    timeoutMs: SUBMIT_TIMEOUT_MS,
    ...(signal ? { signal } : {}),
  });
}

export function submitUpload(
  file: File,
  durationHintS: number | null,
  signal?: AbortSignal,
  language?: string,
  translateTo?: string,
): Promise<SubmitResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  // Display value only. The server probes the received bytes and debits from
  // its own number; a quota system that believes a number from the client is
  // not a quota system (contracts/api.md §3.3).
  if (durationHintS !== null) form.append('duration_hint_s', String(Math.round(durationHintS)));
  // A language page's declared language (contracts/api.md §3.3, 1.17.0): Whisper is told it
  // instead of detecting it. No part at all from every other page, so their uploads are
  // byte for byte what they were.
  if (language) form.append('language', language);
  // The language the speech is translated into (contracts/api.md 1.18.0), from a page that
  // translates (lib/translate-target.ts). No part at all from every other page.
  if (translateTo) form.append('translate_to', translateTo);
  return request<SubmitResponse>('/api/transcribe/upload', {
    method: 'POST',
    form,
    timeoutMs: 120_000,
    ...(signal ? { signal } : {}),
  });
}

/**
 * /auto-sync-subtitles: the audio and the cue timings (never the text), for an offset and a
 * frame-rate scale (contracts/api.md §3.9). The server analyses while the request is open, so
 * it is waited on far longer than a submit: a 90-minute file is a minute or more of work.
 */
export function submitAutosync(
  audio: Blob,
  filename: string,
  cues: readonly (readonly [number, number])[],
  signal?: AbortSignal,
): Promise<AutosyncResponse> {
  const form = new FormData();
  form.append('file', audio, filename);
  form.append('cues', JSON.stringify(cues));
  return request<AutosyncResponse>('/api/autosync', {
    method: 'POST',
    form,
    timeoutMs: 300_000,
    ...(signal ? { signal } : {}),
  });
}

export function getJob(jobId: string, signal?: AbortSignal): Promise<JobResponse> {
  return request<JobResponse>(`/api/jobs/${encodeURIComponent(jobId)}`, {
    ...(signal ? { signal } : {}),
  });
}

/**
 * Anonymous: the route needs no session (contracts/api.md §3.5), and a shared
 * transcript link is opened by people who never submitted anything. Asking for
 * one meant a Turnstile check and a mint before a public document could load,
 * and a browser Turnstile refused saw "that's our bug" on /t/{id}/{format}
 * instead of the transcript (live, 2026-10-05).
 */
export function getTranscript(publicId: string, signal?: AbortSignal): Promise<TranscriptDocument> {
  return request<TranscriptDocument>(`/api/transcripts/${encodeURIComponent(publicId)}`, {
    anonymous: true,
    ...(signal ? { signal } : {}),
  });
}

export function getQuota(signal?: AbortSignal): Promise<QuotaResponse> {
  return request<QuotaResponse>('/api/quota', { ...(signal ? { signal } : {}) });
}

export function getPlatforms(signal?: AbortSignal): Promise<PlatformsResponse> {
  return request<PlatformsResponse>('/api/platforms', { ...(signal ? { signal } : {}) });
}

export { SessionError };
